// Global selector state lives in the URL, so every view is shareable and every chart on a page reads the same slice.

import { type Environment, type FilterParams, pickFilters, toQueryString } from '@growth/client';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export const DEFAULT_RANGE = '30d';

export const RANGE_PRESETS: { value: string; label: string }[] = [
  { value: '1d', label: 'Hoy' },
  { value: '7d', label: 'Últimos 7 días' },
  { value: '14d', label: 'Últimos 14 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
  { value: '180d', label: 'Últimos 180 días' },
];

export const ENV_LABEL: Record<Environment, string> = {
  production: 'Producción',
  staging: 'Staging',
  development: 'Desarrollo',
};

export interface GlobalFilters {
  /** Filters to send to the API (always with an explicit range and environment). */
  params: FilterParams;
  environment: Environment;
  /** Stable string for cache keys. */
  key: string;
  /** Current search string (to keep filters when navigating). */
  search: string;
  set: (patch: Record<string, string | undefined | null>) => void;
}

export function useGlobalFilters(opts: { defaultEnvironment?: Environment } = {}): GlobalFilters {
  const [sp, setSp] = useSearchParams();
  const search = sp.toString();
  const params = useMemo(() => {
    const raw = pickFilters(Object.fromEntries(sp.entries()));
    if (!raw.last && !raw.from && !raw.to) raw.last = DEFAULT_RANGE;
    if (!raw.environment) raw.environment = opts.defaultEnvironment ?? 'production';
    return raw;
  }, [search, opts.defaultEnvironment]);

  const set = useCallback(
    (patch: Record<string, string | undefined | null>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  const key = toQueryString(Object.fromEntries(Object.entries(params).sort(([a], [b]) => a.localeCompare(b))));
  return { params, environment: params.environment ?? 'production', key, search: search ? `?${search}` : '', set };
}

/** Human summary of the active dimension filters (for captions and exports). */
export function describeFilters(f: FilterParams): string {
  const parts: string[] = [];
  if (f.platform) parts.push(`plataforma ${f.platform}`);
  if (f.country) parts.push(`país ${f.country}`);
  if (f.network) parts.push(`fuente ${f.network}`);
  if (f.campaign) parts.push(`campaña ${f.campaign}`);
  if (f.creative) parts.push(`creatividad ${f.creative}`);
  if (f.app_version) parts.push(`versión ${f.app_version}`);
  if (f.experiment) parts.push(`experimento ${f.experiment}`);
  if (f.include_internal) parts.push('incluye internos');
  return parts.join(' · ');
}
